import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useDataProvider } from "react-admin";
import { useRollbar } from "@rollbar/react";
import mixpanel from "mixpanel-browser";

import { COLORS } from "../../common/colors";
import type {
  ComplianceOrg,
  ComplianceOrgCallbacks,
  ComplianceOrgList,
  ComplianceDataset,
  ComplianceProFailed,
  PatientExemptions,
  ProjectComplianceConfig,
  PatientRow
} from "src/providers/compliance/complianceTypes";
import type {
  ComplianceFilters,
  SortField
} from "src/providers/compliance/complianceFilters";
import {
  DEFAULT_FILTERS,
  applyComplianceFilters,
  computeTriage,
  isFilterMatch,
  resolveComplianceView
} from "src/providers/compliance/complianceFilters";
import { sortPatients } from "src/providers/compliance/complianceFilters";
import { medRecentFor } from "src/providers/compliance/complianceMeds";

import { CB, COMPLIANCE_COLORS } from "./complianceColors";
import { OrgSidebar } from "./OrgSidebar";
import { PatientListView } from "./PatientListView";
import { FilterBar } from "./FilterBar";
import { PatientSidebarDrawer } from "./PatientSidebarDrawer";

const filterKey = (org: string, proj?: string | null) =>
  proj ? `${org}:${proj}` : org;

// Every listed project has a config, since the org list is built from them.
function findProjectConfig(
  orgs: ComplianceOrg[],
  orgId: string | null,
  projectId: string | null
): ProjectComplianceConfig | null {
  return (
    orgs.find((o) => o.id === orgId)?.projects.find((p) => p.id === projectId)
      ?.config ?? null
  );
}

const EMPTY_TRIAGE = {
  triaged: null as PatientRow[] | null,
  streamMissingCounts: {} as Record<string, number>,
  computeFailed: false
};

const ComplianceOverview = () => {
  const dataProvider = useDataProvider();
  const rollbar = useRollbar();

  const [orgs, setOrgs] = useState<ComplianceOrg[]>([]);
  // Null if they failed to load.
  const [exemptions, setExemptions] = useState<PatientExemptions | null>(null);
  const [dataset, setDataset] = useState<ComplianceDataset | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null
  );
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    null
  );
  const [filters, setFilters] = useState<ComplianceFilters>(DEFAULT_FILTERS);
  const savedFilters = useRef(new Map<string, ComplianceFilters>());
  const savedProjectIds = useRef(new Map<string, string | null>());
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const selectedOrgIdRef = useRef(selectedOrgId);
  selectedOrgIdRef.current = selectedOrgId;
  const selectedProjectIdRef = useRef(selectedProjectId);
  selectedProjectIdRef.current = selectedProjectId;
  const orgsRef = useRef(orgs);
  orgsRef.current = orgs;
  const exemptionsRef = useRef(exemptions);
  exemptionsRef.current = exemptions;

  useEffect(() => {
    mixpanel.track("ComplianceOverview");
  }, []);

  useEffect(() => {
    let cancelled = false;
    setOrgsLoading(true);
    const callbacks: ComplianceOrgCallbacks = {
      // A skipped project or org would otherwise just be missing from the list.
      onSkipped: (resource, id, err) =>
        rollbar.warning(
          `Compliance org list skipped ${resource} ${id}`,
          err as Error
        ),
      // The lists still show, with every patient checked as if they had no exemption.
      onExemptionsFailed: (err) =>
        rollbar.error("Compliance exemptions load failed", err as Error)
    };
    dataProvider
      .getComplianceOrgs(callbacks)
      .then((result: ComplianceOrgList) => {
        if (!cancelled) {
          setOrgs(result.orgs);
          setExemptions(result.exemptions);
          setOrgsLoading(false);
        }
      })
      .catch((err: unknown) => {
        rollbar.error("Compliance org list load failed", err as Error);
        if (!cancelled) setOrgsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataProvider, rollbar]);

  useEffect(() => {
    if (!selectedOrgId || !selectedProjectId) {
      setDataset(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const projectConfig = findProjectConfig(
      orgsRef.current,
      selectedOrgId,
      selectedProjectId
    );
    if (!projectConfig) {
      setDataset(null);
      setLoading(false);
      return;
    }
    // The list still shows; these patients' medication and bowel movement checks are skipped.
    const onProFailed: ComplianceProFailed = (patientIds, err) =>
      rollbar.error(
        `Compliance PRO load failed for ${patientIds.length} patients`,
        err as Error
      );
    dataProvider
      .getComplianceData(
        {
          orgId: selectedOrgId,
          projectId: selectedProjectId,
          projectConfig,
          exemptions: exemptionsRef.current
        },
        onProFailed
      )
      .then((result: ComplianceDataset) => {
        if (!cancelled) {
          setDataset(result);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          rollbar.error("Compliance data load failed", err as Error);
          setDataset(null);
          setLoading(false);
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dataProvider, rollbar, selectedOrgId, selectedProjectId]);

  // Clear patient selection when org/project changes
  useEffect(() => {
    setSelectedPatientId(null);
  }, [selectedOrgId, selectedProjectId]);

  const selectedOrg = orgs.find((o) => o.id === selectedOrgId);
  // Loaded with the org list, so the filter bar shows the project's checks before its
  // patients arrive.
  const projectConfig = findProjectConfig(
    orgs,
    selectedOrgId,
    selectedProjectId
  );

  // Memoized on the three settings it reads, so sorting or filtering doesn't re-run triage.
  const { visibleStreams, medBasis, triageConfig } = filters;
  const view = useMemo(
    () =>
      projectConfig
        ? resolveComplianceView(projectConfig, {
            visibleStreams,
            medBasis,
            triageConfig
          })
        : null,
    [projectConfig, visibleStreams, medBasis, triageConfig]
  );

  // Rows keep their identity across sort and filter changes, so unchanged rows don't re-render.
  // One `now` for triage, the cards and the cells, so they can't land on different days.
  const { triaged, streamMissingCounts, computeFailed, now } = useMemo(() => {
    const now = new Date();
    if (!dataset || !view) return { ...EMPTY_TRIAGE, now };
    try {
      // Triage, the med column and the filter card counts all read the selected basis.
      const rows = dataset.patients.map((p) => {
        const withBasis = {
          ...p,
          medRecent: medRecentFor(
            p,
            view.medBasis,
            view.projectConfig.studyMedications,
            now
          )
        };
        return {
          ...withBasis,
          ...computeTriage(withBasis, view.triageConfig, view.columns, now)
        };
      });
      const counts: Record<string, number> = {};
      for (const p of rows) {
        for (const stream of view.columns) {
          if (isFilterMatch(p, stream, view.triageConfig, now)) {
            counts[stream] = (counts[stream] ?? 0) + 1;
          }
        }
      }
      return {
        triaged: sortPatients(rows, "triage", "desc"),
        streamMissingCounts: counts,
        computeFailed: false,
        now
      };
    } catch (err) {
      rollbar.error("Compliance triage computation failed", err as Error);
      return { ...EMPTY_TRIAGE, computeFailed: true, now };
    }
  }, [dataset, view, rollbar]);

  const filteredDataset = useMemo(
    () =>
      dataset && triaged && view
        ? {
            ...dataset,
            patients: applyComplianceFilters(
              triaged,
              filters,
              view.triageConfig,
              now
            )
          }
        : null,
    [dataset, triaged, filters, view, now]
  );
  const preFilterCount = dataset?.patients.length ?? 0;

  const displayError =
    error ??
    (computeFailed
      ? "Something went wrong working out compliance for this project."
      : null);

  const isFiltered =
    filters.triageFilter !== "everyone" ||
    filters.missingDataStream !== null ||
    filters.createdWithinDays !== null ||
    filters.outreachWithinDays !== null;

  const handleOrgSelect = useCallback((orgId: string) => {
    const prev = selectedOrgIdRef.current;
    const prevProject = selectedProjectIdRef.current;
    if (prev) {
      savedFilters.current.set(
        filterKey(prev, prevProject),
        filtersRef.current
      );
      savedProjectIds.current.set(prev, prevProject);
    }
    const next = prev === orgId ? null : orgId;
    setSelectedOrgId(next);
    setDataset(null);
    setError(null);
    if (next) {
      // Open the last project viewed in this org, else its first project.
      const restoredProject =
        savedProjectIds.current.get(next) ??
        orgsRef.current.find((o) => o.id === next)?.projects[0]?.id ??
        null;
      setLoading(restoredProject !== null);
      setSelectedProjectId(restoredProject);
      setFilters(
        savedFilters.current.get(filterKey(next, restoredProject)) ??
          DEFAULT_FILTERS
      );
    } else {
      setSelectedProjectId(null);
    }
  }, []);

  const handleProjectSelect = useCallback((projectId: string) => {
    const orgId = selectedOrgIdRef.current;
    const prevProject = selectedProjectIdRef.current;
    if (!orgId || projectId === prevProject) return;
    savedFilters.current.set(filterKey(orgId, prevProject), filtersRef.current);
    // Drop the previous project's data so its patients aren't shown with this project's
    // settings while the new data loads.
    setDataset(null);
    setError(null);
    setLoading(true);
    setSelectedProjectId(projectId);
    setFilters(
      savedFilters.current.get(filterKey(orgId, projectId)) ?? DEFAULT_FILTERS
    );
  }, []);

  const handlePatientClick = useCallback((patientId: string) => {
    setSelectedPatientId(patientId);
  }, []);
  const handleSidebarClose = useCallback(() => setSelectedPatientId(null), []);

  const handleSortChange = useCallback((field: SortField) => {
    setFilters((prev) => {
      if (prev.sortField === field) {
        return {
          ...prev,
          sortDirection: prev.sortDirection === "asc" ? "desc" : "asc"
        };
      }
      return { ...prev, sortField: field, sortDirection: "desc" };
    });
  }, []);

  const selectedPatient =
    triaged && selectedPatientId
      ? triaged.find((p) => p.patientId === selectedPatientId) ?? null
      : null;

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)" }}>
      {orgsLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            backgroundColor: COMPLIANCE_COLORS.PAGE_BG
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <OrgSidebar
            orgs={orgs}
            selectedOrgId={selectedOrgId}
            onOrgSelect={handleOrgSelect}
          />
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              backgroundColor: COMPLIANCE_COLORS.PAGE_BG
            }}
          >
            {/* Padding lives inside the scroll container so the pinned header sticks flush to its top */}
            <Box sx={{ px: 3, py: 2.5 }}>
              {!selectedOrgId && (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 400,
                      fontFamily: "Work Sans, sans-serif",
                      color: COLORS.GREY_600
                    }}
                  >
                    Select an organization to view compliance data
                  </Typography>
                </Box>
              )}

              {selectedOrgId && selectedOrg && view && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: 400,
                      color: COLORS.PRIMARY_1200,
                      mb: 0,
                      lineHeight: 1.15
                    }}
                  >
                    {selectedOrg.displayName}
                  </Typography>
                  <FilterBar
                    projects={selectedOrg.projects}
                    selectedProjectId={selectedProjectId}
                    onProjectSelect={handleProjectSelect}
                    filters={filters}
                    view={view}
                    onFiltersChange={setFilters}
                    totalPatients={preFilterCount}
                    streamMissingCounts={streamMissingCounts}
                  />
                </Box>
              )}

              {selectedOrg && selectedOrg.projects.length === 0 && (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontFamily: "Work Sans, sans-serif",
                      color: COLORS.GREY_600
                    }}
                  >
                    This organization has no projects
                  </Typography>
                </Box>
              )}

              {selectedOrgId && loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress />
                </Box>
              )}

              {selectedOrgId && !loading && displayError && (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: CB.VERMILLION_TEXT
                    }}
                  >
                    {displayError}
                  </Typography>
                </Box>
              )}

              {selectedOrgId &&
                !loading &&
                !displayError &&
                filteredDataset &&
                exemptions === null && (
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: CB.VERMILLION_TEXT,
                      mb: 1
                    }}
                  >
                    Exemptions couldn&apos;t be loaded, so exempt patients may
                    show as out of compliance
                  </Typography>
                )}

              {selectedOrgId &&
                !loading &&
                !displayError &&
                view &&
                filteredDataset &&
                filteredDataset.proFailedCount > 0 &&
                // Only matters when a check that needs PRO data is shown.
                view.columns.some(
                  (c) => c === "medication" || c === "bowel_movement"
                ) && (
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: CB.VERMILLION_TEXT,
                      mb: 1
                    }}
                  >
                    Medication and bowel movement data couldn&apos;t be loaded
                    for {filteredDataset.proFailedCount}{" "}
                    {filteredDataset.proFailedCount === 1
                      ? "patient"
                      : "patients"}
                  </Typography>
                )}

              {selectedOrgId &&
                !loading &&
                !displayError &&
                view &&
                filteredDataset && (
                  <PatientListView
                    dataset={filteredDataset}
                    onPatientClick={handlePatientClick}
                    selectedPatientId={selectedPatient?.patientId ?? null}
                    isFiltered={isFiltered}
                    visibleStreams={view.columns}
                    focusedStream={filters.missingDataStream}
                    sortField={filters.sortField}
                    sortDirection={filters.sortDirection}
                    onSortChange={handleSortChange}
                    triageConfig={view.triageConfig}
                    now={now}
                  />
                )}
            </Box>
          </Box>
          {selectedOrgId && selectedProjectId && view && (
            <PatientSidebarDrawer
              patient={selectedPatient}
              view={view}
              filters={filters}
              now={now}
              orgId={selectedOrgId}
              projectId={selectedProjectId}
              onClose={handleSidebarClose}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ComplianceOverview;
